// Companion/blocks/studySetFlashcardRun.ts
//
// The adapter between a study set's wire cards and the flashcard run engine
// (ORA-687). The in-chat deck runs the *same* reducer as the full flashcard
// screen, so a card rated Again in the thread comes back in the thread and a
// card is only counted when it has left the queues — but it runs it locally,
// through `useReducer`, because the drawer opens over the study routes and a
// full session may be holding `state.flashcardRun` on the page behind it.

import { getNewScheduling } from '@/services/flashcardScheduling';
import {
  buildFlashcardRunServeEntries,
  type FlashcardRunBootstrapPayload,
  type FlashcardRunQueueKey,
} from '@/slices/flashcardRun/flashcardRunSlice';
import type { Flashcard, FlashcardScheduling } from '@/types/Flashcard';
import type { ChatStudySetFlashcard } from '@/types/chatBlocks';

/**
 * The scheduling a never-seen card starts from. It is the backend's own
 * bootstrap default for a card with no scheduling row (`flashcardRunHelpers`),
 * so a new card first seen in chat takes the same first interval it would have
 * taken in study.
 */
export const newCardScheduling = (): FlashcardScheduling => ({
  due: new Date().toISOString(),
  stability: 3,
  difficulty: 0,
  elapsedDays: 0,
  scheduledDays: 0,
  reps: 0,
  lapses: 0,
  state: 0,
  lastReview: new Date().toISOString(),
  difficultyIndex: 0,
});

/**
 * A set card as the run engine wants it. The engine reads the id and the
 * scheduling — the deck draws the face from the set payload — so this carries
 * nothing else the wire card does not already have.
 */
const toRunFlashcard = (card: ChatStudySetFlashcard): Flashcard => ({
  id: card.id,
  front: card.front,
  back: card.back,
  scheduling: card.scheduling ?? newCardScheduling(),
  isQAed: true,
});

const isNewCard = (card: ChatStudySetFlashcard) =>
  !card.scheduling || card.scheduling.state === 0;

/**
 * The bootstrap for a run over one set's cards: never-seen cards in the new
 * queue, everything else due. Pure — the caller seeds its own reducer with it
 * rather than dispatching into the store.
 */
export const bootstrapFlashcardRunFromSetCards = (params: {
  setId: string;
  cards: ChatStudySetFlashcard[];
  completedCardIds?: string[];
}): FlashcardRunBootstrapPayload => {
  const completed = new Set(params.completedCardIds ?? []);
  const queued = params.cards.filter((card) => !completed.has(card.id));
  const newQueueIds = queued.filter(isNewCard).map((card) => card.id);

  const priorityDueIds = queued
    .filter((card) => !isNewCard(card))
    .map((card) => card.id);

  const head = buildFlashcardRunServeEntries({
    priorityDueIds,
    newQueueIds,
    deferredDueIds: [],
    order: 'due',
  })[0];

  return {
    scope: { kind: 'set', setId: params.setId },
    scopeKey: `set:${params.setId}`,
    studySessionId: null,
    priorityDueIds,
    newQueueIds,
    deferredDueIds: [],
    completedIds: params.cards
      .filter((card) => completed.has(card.id))
      .map((card) => card.id),
    currentCardId: head?.id ?? null,
    currentCardType: head?.type ?? 'none',
    order: 'due',
    // Cards the server had already marked done seed `completedIds` but not the
    // count: the count is what the student clears here, and it is what the
    // deck's closing line reports.
    completedCount: 0,
    totalCount: params.cards.length,
    hydratedCards: params.cards.map(toRunFlashcard),
  };
};

/** The FSRS step for a set card, on the wording the block was served. */
export const nextSetCardScheduling = (
  card: ChatStudySetFlashcard,
  response: number,
  desiredRetention?: number
) =>
  getNewScheduling(
    { scheduling: card.scheduling ?? newCardScheduling() },
    response,
    {
      // The block is served one presented variant rather than the card's whole
      // list, so the count comes off the payload — the same number
      // `useFlashcardResponse` gets from `variants.length`. Anything else would
      // freeze the card on one wording in chat and step it in study.
      variantCount: card.variantCount,
      desiredRetention,
    }
  );

/** The queue the card is being rated out of, for the reducer's undo entry. */
export const sourceQueueFor = (
  run: { newQueueIds: string[]; deferredDueIds: string[] },
  cardId: string
): FlashcardRunQueueKey => {
  if (run.newQueueIds.includes(cardId)) return 'newQueueIds';
  if (run.deferredDueIds.includes(cardId)) return 'deferredDueIds';
  return 'priorityDueIds';
};
